// Activity logging hook for user management features
import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { rbacService } from '../../backend/services/rbac';
import type { FeatureName } from '../../shared/types/user-management';

export const useActivityLogger = () => {
  const { user } = useAuth();

  const logActivity = useCallback(async (
    actionType: string,
    resourceType?: string,
    resourceId?: string,
    resourceName?: string,
    details: Record<string, any> = {}
  ) => {
    if (!user?.id) return;

    try {
      await rbacService.logUserActivity(
        user.id,
        actionType,
        resourceType,
        resourceId,
        resourceName,
        details,
        // Get IP and user agent from browser if available
        undefined, // IP address would need to be obtained from server
        navigator.userAgent
      );
    } catch (error) {
      console.warn('Failed to log user activity:', error);
      // Don't throw error to avoid disrupting user experience
    }
  }, [user?.id]);

  const trackFeature = useCallback(async (featureName: FeatureName) => {
    if (!user?.id) return;

    try {
      await rbacService.trackFeatureUsage(user.id, featureName);
    } catch (error) {
      console.warn('Failed to track feature usage:', error);
    }
  }, [user?.id]);

  const logUserManagementAction = useCallback(async (
    action: 'create' | 'update' | 'delete' | 'invite' | 'suspend' | 'activate',
    targetUserId?: string,
    targetUserEmail?: string,
    details: Record<string, any> = {}
  ) => {
    await logActivity(
      action,
      'user',
      targetUserId,
      targetUserEmail,
      details
    );
  }, [logActivity]);

  const logRoleManagementAction = useCallback(async (
    action: 'create' | 'update' | 'delete' | 'assign' | 'revoke',
    roleId?: string,
    roleName?: string,
    details: Record<string, any> = {}
  ) => {
    await logActivity(
      action,
      'role',
      roleId,
      roleName,
      details
    );
  }, [logActivity]);

  return {
    logActivity,
    trackFeature,
    logUserManagementAction,
    logRoleManagementAction
  };
};
